<script setup lang="ts">
	// Host for useConfirm(). Mounted once in the default layout.
	const { request, settle } = useConfirm();
	const dialog = ref<HTMLDialogElement | null>(null);
	const titleId = useId();
	const messageId = useId();
	let previousFocus: HTMLElement | null = null;

	watch(request, async (open) => {
		if (!import.meta.client) return;
		if (open && !dialog.value?.open) previousFocus = document.activeElement as HTMLElement | null;
		await nextTick();
		if (open) {
			if (!dialog.value?.open) dialog.value?.showModal();
		} else if (dialog.value?.open) {
			dialog.value.close();
			if (previousFocus?.isConnected) previousFocus.focus();
			previousFocus = null;
		}
	});
</script>

<template>
	<Teleport to="body">
		<dialog ref="dialog" class="confirm-dialog" :aria-labelledby="titleId" :aria-describedby="request?.message ? messageId : undefined" @cancel.prevent="settle(false)" @click="settle(false)">
			<div v-if="request" class="confirm-panel" @click.stop>
				<h2 :id="titleId">{{ request.title }}</h2>
				<p v-if="request.message" :id="messageId">{{ request.message }}</p>
				<div class="confirm-actions">
					<button type="button" class="cancel" autofocus @click="settle(false)">{{ request.cancelLabel || "Cancel" }}</button>
					<button type="button" class="confirm" :class="{ danger: request.danger }" @click="settle(true)">{{ request.confirmLabel || "OK" }}</button>
				</div>
			</div>
		</dialog>
	</Teleport>
</template>

<style scoped lang="scss">
	.confirm-dialog {
		border: 0;
		padding: 0;
		margin: auto;
		width: min(420px, calc(100vw - 32px));
		max-width: calc(100vw - 32px);
		background: transparent;
		color: var(--text);
		overflow: visible;
		&::backdrop {
			background: rgba(17, 18, 20, 0.28);
		}
		&[open] .confirm-panel {
			animation: confirm-in var(--dur-fast, 140ms) var(--ease-out, ease-out);
		}
	}
	.confirm-panel {
		padding: 22px 22px 18px;
		background: var(--bg-surface, #fff);
		border: 1px solid var(--border);
		border-radius: var(--radius-lg, 12px);
		box-shadow: var(--shadow-lg);
		font-size: 13px;
	}
	h2 {
		margin: 0;
		font-size: 16px;
		font-weight: 600;
		line-height: 1.35;
	}
	p {
		margin: 8px 0 0;
		line-height: 1.55;
		color: var(--text-secondary);
	}
	.confirm-actions {
		display: flex;
		justify-content: flex-end;
		gap: 8px;
		margin-top: 20px;
	}
	button {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 9px 14px;
		border-radius: var(--radius-sm);
		font: inherit;
		font-size: 13px;
		font-weight: 600;
		white-space: nowrap;
		cursor: pointer;
		transition: background var(--dur-fast) var(--ease-out), border-color var(--dur-fast) var(--ease-out);
		&:focus-visible {
			outline: 2px solid var(--focus);
			outline-offset: 2px;
		}
	}
	.cancel {
		background: var(--bg-surface, #fff);
		border: 1px solid var(--border);
		color: var(--text);
		font-weight: 500;
		&:hover {
			background: var(--bg-subtle);
			border-color: var(--border-strong);
		}
	}
	.confirm {
		background: var(--text);
		border: 1px solid var(--text);
		color: var(--text-inverse);
		&.danger {
			background: var(--accent);
			border-color: var(--accent);
			&:hover {
				background: var(--accent-hover);
			}
		}
	}
	@keyframes confirm-in {
		from {
			opacity: 0;
			transform: translateY(4px) scale(0.98);
		}
	}
	@media (max-width: 640px) {
		.confirm-actions button {
			flex: 1;
		}
	}
</style>
